import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export enum adaptiveAssessmentStateType {
  START = 'START',
  AWAITING_USER_START = 'AWAITING USER START',
  ATTEMPT = 'ATTEMPT',
  FINISHED = 'FINISHED',
}

export enum attemptQuestionType {
  CODING = 'Coding',
  OBJECTIVE = 'Objective',
}

export enum questionStateType {
  CONFIRMATION_PENDING = 'CONFIRMATION PENDING',
  SUBMISSION_PENDING = 'SUBMISSION PENDING',
  CLEAN_STATE = 'CLEAN STATE',
}

export enum chatMessageOwner {
  CANDIDATE = 'Candidate',
  CANDIDATEFULL = 'Candidate_full',
  BOT = 'Bot',
}

export enum messageStringType {
  STRING = 'STRING',
  OPTIONS = 'OPTIONS',
  DESCRIPTION = 'DESCRIPTION',
}

export interface messageType {
  owner: chatMessageOwner;
  type: messageStringType;
  message: string;
  question_id?: string;
  audioUrl?: string;
  isCapture?: boolean;
}

export interface userDetailsType {
  name: string;
  email: string;
}
export interface AIBotAssessmentState {
  history: messageType[];
  current_question_number: number;
  total_question: number;
  total_time: number;
  time_remaining: number;
  assessment_name: string;
  company_name: string;
  question_answered: number;
  current_state: adaptiveAssessmentStateType;
  question_state: questionStateType;
  questionData: any;
  language: any;
  messages: messageType[];
  enableChat: boolean;
  assessmentStarted: boolean;
  currentQuestionType: attemptQuestionType;
  questionLoading: boolean;
  options: string[];
  showOptions: boolean;
  userDetails: userDetailsType;
  codeRunning: boolean;
  startMessages: string[];
  recordingUser: boolean;
  chatIndex: number;
  landscapeMode: boolean;
  meetingTitle: string;
  isToggleTranscription: boolean;
  isInterviewerMinWidth: boolean;
  staticImage: string;
  botStaticImage: string;
  botDynamicImage: string;
  interviewAudioType: boolean;
  botName: string;
  defaultGracePeriod: number;
  speakingLanguage: string;
}

const initialState: AIBotAssessmentState = {
  history: [],
  current_question_number: 0,
  total_question: 0,
  total_time: 0,
  assessment_name: '',
  interviewAudioType: false,
  language: null,
  chatIndex: -1,
  company_name: '',
  question_answered: 0,
  time_remaining: Infinity,
  current_state: adaptiveAssessmentStateType.START,
  question_state: questionStateType.CLEAN_STATE,
  meetingTitle: '',
  questionData: {
    id: null,
  },
  messages: [],
  enableChat: false,
  assessmentStarted: false,
  currentQuestionType: attemptQuestionType.OBJECTIVE,
  questionLoading: false,
  showOptions: false,
  options: [],
  landscapeMode: false,
  userDetails: {
    name: '',
    email: '',
  },
  codeRunning: false,
  startMessages: [],
  recordingUser: false,
  isToggleTranscription: false,
  staticImage: '',
  botStaticImage: '',
  botDynamicImage: '',
  isInterviewerMinWidth: false,
  botName: 'HireHunch Interviewer',
  defaultGracePeriod: 5,
  speakingLanguage: 'en-IN',
};

export const aiBotAssessmentSlice = createSlice({
  name: 'aiAssessmentSlice',
  initialState,
  reducers: {
    setBotAssessmentInfo: (
      state,
      action: PayloadAction<AIBotAssessmentState>
    ) => {
      const {
        total_question,
        total_time,
        assessment_name,
        company_name,
        question_answered,
        userDetails,
        meetingTitle,
      } = action.payload;
      state.total_question = total_question;
      state.total_time = total_time;
      state.assessment_name = assessment_name;
      state.company_name = company_name;
      state.question_answered = question_answered;
      state.current_question_number = question_answered + 1;
      state.userDetails = userDetails;
      state.meetingTitle = meetingTitle;
    },
    setCurrentState: (
      state,
      action: PayloadAction<adaptiveAssessmentStateType>
    ) => {
      state.current_state = action.payload;
    },
    setQuestionState: (state, action: PayloadAction<questionStateType>) => {
      state.question_state = action.payload;
    },
    setAITimeRemaining: (state, action: PayloadAction<number>) => {
      state.time_remaining = action.payload;
    },
    setAIBotName: (state, action: PayloadAction<string>) => {
      state.botName =
        (action.payload ?? '').length > 0
          ? action.payload
          : 'HireHunch Interviewer';
    },

    setAIGracePeriod: (state, action: PayloadAction<number>) => {
      state.defaultGracePeriod = action.payload;
    },
    setChatIndex: (state, action: PayloadAction<number>) => {
      state.chatIndex = action.payload;
    },
    setAIRecordingUser: (state, action: PayloadAction<boolean>) => {
      state.recordingUser = action.payload;
    },
    setAIInterviewAudioType: (state, action: PayloadAction<boolean>) => {
      state.interviewAudioType = action.payload;
    },
    setLandscapeMode: (state, action: PayloadAction<boolean>) => {
      state.landscapeMode = action.payload;
    },
    setAIQuestionAnswered: (state, action: PayloadAction<number>) => {
      state.question_answered = action.payload;
      state.current_question_number = action.payload + 1;
    },
    setCurrentQuestion: (state, action: PayloadAction<number>) => {
      state.current_question_number = action.payload;
    },
    setAIQuestionData: (state, action: PayloadAction<any>) => {
      state.questionData = action.payload;
    },
    resetQuestionData: (state) => {
      state.questionData = null;
    },
    setAIMessageAtIndex: (
      state,
      action: PayloadAction<{ message: messageType; index: number }>
    ) => {
      if (
        action.payload.index >= state.messages.length ||
        action.payload.index < 0
      )
        return;
      state.messages[action.payload.index] = action.payload.message;
    },
    pushAIMessage: (state, action: PayloadAction<messageType[]>) => {
      if (action.payload.length > 0) {
        action.payload.forEach((el) => state.messages.push(el));
      }
    },
    resetMessage: (state) => {
      state.messages = [];
    },
    setChatEnabled: (state, action: PayloadAction<boolean>) => {
      state.enableChat = action.payload;
    },
    startAIAssessment: (state) => {
      state.assessmentStarted = true;
    },
    stopAIAssessment: (state) => {
      state.assessmentStarted = false;
    },
    setQuestionType: (state, action: PayloadAction<attemptQuestionType>) => {
      state.currentQuestionType = action.payload;
    },
    setQuestionloading: (state, action: PayloadAction<boolean>) => {
      state.questionLoading = action.payload;
    },
    setOptions: (state, action: PayloadAction<string[]>) => {
      state.options = action.payload;
    },
    setShowOptions: (state, action: PayloadAction<boolean>) => {
      state.showOptions = action.payload;
    },
    setUserDetails: (state, action: PayloadAction<userDetailsType>) => {
      state.userDetails = action.payload;
    },
    setLanguage: (state, action: PayloadAction<any>) => {
      state.language = action.payload;
    },
    setAdaptiveCodeRunning: (state, action: PayloadAction<boolean>) => {
      state.codeRunning = action.payload;
    },
    setAdaptiveTotalQuestion: (state, action: PayloadAction<number>) => {
      state.total_question = action.payload;
    },
    setStartMessages: (state, action: PayloadAction<string[]>) => {
      state.startMessages = action.payload;
    },
    setSpeakingLanguage: (state, action: PayloadAction<string>) => {
      state.speakingLanguage = action.payload;
    },
    pushToHistory: (state, action: PayloadAction<messageType>) => {
      state.history = [...state.history, action.payload];
    },
    toggleTranscriptionChat: (state) => {
      state.isToggleTranscription = !state.isToggleTranscription;
    },
    setStaticImage: (state, action: PayloadAction<string>) => {
      state.staticImage = action.payload;
    },
    setBotStaticImage: (state, action: PayloadAction<string>) => {
      state.botStaticImage = action.payload;
    },
    setBotDynamicImage: (state, action: PayloadAction<string>) => {
      state.botDynamicImage = action.payload;
    },
    toggleIsInterviewerMinWidth: (state, action) => {
      state.isInterviewerMinWidth = action.payload;
    },
  },
});

export const {
  setBotAssessmentInfo,
  setCurrentState,
  setQuestionState,
  setAITimeRemaining,
  setAIQuestionAnswered,
  setChatIndex,
  pushAIMessage,
  setAIMessageAtIndex,
  setAIRecordingUser,
  setLandscapeMode,
  setCurrentQuestion,
  resetMessage,
  resetQuestionData,
  setAIQuestionData,
  startAIAssessment,
  stopAIAssessment,
  setQuestionloading,
  setQuestionType,
  setOptions,
  setBotDynamicImage,
  setAIInterviewAudioType,
  setStaticImage,
  setBotStaticImage,
  setShowOptions,
  setUserDetails,
  setLanguage,
  setAdaptiveCodeRunning,
  setAdaptiveTotalQuestion,
  setAIBotName,
  setSpeakingLanguage,
  setAIGracePeriod,
  setStartMessages,
  pushToHistory,
  toggleTranscriptionChat,
  toggleIsInterviewerMinWidth,
} = aiBotAssessmentSlice.actions;

export default aiBotAssessmentSlice.reducer;
